import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Progress } from "../components/ui/progress";
import { User, Archive, Heart, TrendingUp, Gem, Star, Clock, ImageOff, Share2, ExternalLink, Trophy, UserPlus, UserCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import HaulCard from "../components/HaulCard";
import ArchetypeCard from "../components/ArchetypeCard";

function StatCard({ label, value, icon: Icon, color = "text-zinc-300" }) {
  return (
    <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4 text-center">
      <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
      <p className={`font-mono text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
    </div>
  );
}

function CollectionGrid({ releases, onNavigate }) {
  if (!releases?.length) return <p className="text-zinc-600 text-sm text-center py-8">No models in collection yet</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
      {releases.map((r) => (
        <div key={r.release_id} onClick={() => onNavigate(`/catalog/${r.release_id}`)} className="group cursor-pointer bg-zinc-900 border border-zinc-800/50 rounded-xl overflow-hidden hover:border-zinc-700 transition-all hover:-translate-y-0.5">
          <div className="aspect-[4/3] bg-zinc-800 overflow-hidden">
            {r.model_image ? (
              <img src={resolveImageUrl(r.model_image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><ImageOff className="w-6 h-6 text-zinc-700" /></div>
            )}
          </div>
          <div className="p-2">
            <p className="text-[10px] text-zinc-600 font-mono">{r.khmg_number}</p>
            <p className="text-xs text-zinc-200 font-medium truncate">{r.release_name}</p>
            {r.estimated_market_value && <p className="text-xs font-mono text-rose-500 mt-0.5">${r.estimated_market_value}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function WishlistSection({ wishlist, onNavigate }) {
  if (!wishlist?.length) return null;
  return (
    <div className="space-y-2">
      {wishlist.map((w) => {
        const r = w.release || {};
        return (
          <div key={w.wishlist_id} onClick={() => onNavigate(`/catalog/${r.release_id}`)} className="flex items-center gap-3 p-3 bg-zinc-900/60 border border-white/5 rounded-lg cursor-pointer hover:border-zinc-700 transition-colors">
            {r.model_image ? (
              <img src={resolveImageUrl(r.model_image)} alt="" className="w-10 h-10 rounded object-cover" />
            ) : (
              <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center"><ImageOff className="w-4 h-4 text-zinc-700" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-200 truncate">{r.release_name}</p>
              <p className="text-[11px] text-zinc-500">{r.vehicle}</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${w.priority === "Grail" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : w.priority === "High" ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-zinc-700/50 text-zinc-400 border-zinc-600"}`}>
              {w.priority}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RecentActivity({ activity, onNavigate }) {
  if (!activity?.length) return null;
  return (
    <div className="space-y-2">
      {activity.map((a, i) => {
        const r = a.release || {};
        return (
          <div key={`${a.entry_id}-${i}`} onClick={() => onNavigate(`/catalog/${r.release_id}`)} className="flex items-center gap-3 p-2 hover:bg-zinc-800/30 rounded-lg cursor-pointer transition-colors">
            <Clock className="w-3 h-3 text-zinc-600 flex-shrink-0" />
            <p className="text-sm text-zinc-300 truncate flex-1">{r.release_name || a.release_id}</p>
            <span className="text-[10px] text-zinc-600">{a.date_added?.slice(0, 10)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PublicProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user: viewer } = useAuth();
  const [profile, setProfile] = useState(null);
  const [hauls, setHauls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [followState, setFollowState] = useState({
    following: false, follows_you: false, followers_count: 0, following_count: 0,
  });
  const [followLoading, setFollowLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const [{ data: profileData }, haulsRes] = await Promise.all([
        api.get(`/public/profile/${slug}`),
        api.get(`/public/profile/${slug}/hauls?limit=10`).catch(() => ({ data: { posts: [] } })),
      ]);
      setProfile(profileData);
      setHauls(haulsRes.data.posts || []);
      if (profileData?.user?.user_id) {
        try {
          const { data: fs } = await api.get(`/follow/status/${profileData.user.user_id}`);
          setFollowState(fs);
        } catch { /* optional */ }
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Profile link copied!");
  };

  const handleToggleFollow = async () => {
    if (!viewer) { navigate("/login"); return; }
    if (!profile?.user?.user_id) return;
    const targetId = profile.user.user_id;
    setFollowLoading(true);
    try {
      if (followState.following) {
        const { data } = await api.delete(`/follow/${targetId}`);
        setFollowState((s) => ({ ...s, following: false, followers_count: data.followers_count ?? Math.max(0, s.followers_count - 1) }));
      } else {
        const { data } = await api.post(`/follow/${targetId}`);
        setFollowState((s) => ({ ...s, following: true, followers_count: data.followers_count ?? s.followers_count + 1 }));
        toast.success(`Following ${profile.user.name}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow state");
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center text-center p-6">
        <User className="w-16 h-16 text-zinc-700 mb-4" />
        <h1 className="text-2xl font-heading font-bold text-zinc-300">Profile Not Found</h1>
        <p className="text-zinc-500 mt-2">This collector profile doesn't exist or is set to private.</p>
        <Button onClick={() => navigate("/login")} className="mt-6 bg-rose-600 hover:bg-rose-500 text-white">Go to App</Button>
      </div>
    );
  }

  const { user, stats, collection, wishlist, recent_activity } = profile;

  return (
    <div data-testid="public-profile-page" className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-gradient-to-b from-rose-950/30 to-zinc-950 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6">
            <div className="w-24 h-24 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center overflow-hidden shadow-[0_0_30px_rgba(225,29,72,0.15)]">
              {user.profile_photo ? (
                <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-zinc-500" />
              )}
            </div>
            <div className="text-center sm:text-left flex-1">
              <h1 className="font-heading text-3xl font-bold text-zinc-100">{user.name}</h1>
              {user.bio && <p className="text-zinc-400 text-sm mt-1">{user.bio}</p>}
              <p className="text-zinc-600 text-xs mt-2">@{user.profile_slug || user.user_id} &middot; Member since {new Date(user.created_at).toLocaleDateString()}</p>
              <div className="flex items-center justify-center sm:justify-start gap-4 mt-2 text-xs text-zinc-400">
                <span data-testid="profile-followers-count"><strong className="text-zinc-200 font-mono">{followState.followers_count}</strong> {followState.followers_count === 1 ? "follower" : "followers"}</span>
                <span data-testid="profile-following-count"><strong className="text-zinc-200 font-mono">{followState.following_count}</strong> following</span>
                {followState.follows_you && (
                  <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded" data-testid="follows-you-badge">Follows you</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {viewer && viewer.user_id !== user.user_id && (
                <Button
                  data-testid="follow-btn"
                  onClick={handleToggleFollow}
                  disabled={followLoading}
                  className={
                    followState.following
                      ? "bg-zinc-900 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-rose-500/40 hover:text-rose-400"
                      : "bg-rose-600 hover:bg-rose-500 text-white border border-rose-500"
                  }
                >
                  {followState.following ? (<><UserCheck className="w-4 h-4 mr-1" /> Following</>) : (<><UserPlus className="w-4 h-4 mr-1" /> Follow</>)}
                </Button>
              )}
              <Button data-testid="share-profile-btn" onClick={handleShare} variant="outline" className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800">
                <Share2 className="w-4 h-4 mr-1" /> Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Live archetype */}
        <ArchetypeCard
          userIdOrSlug={profile.user.profile_slug || profile.user.user_id}
          owner={{ name: profile.user.name, profile_slug: profile.user.profile_slug, profile_photo: profile.user.profile_photo }}
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Owned" value={stats.total_owned} icon={Archive} color="text-blue-400" />
          <StatCard label="Completion" value={`${stats.completion}%`} icon={TrendingUp} color="text-emerald-400" />
          <StatCard label="Value" value={`$${stats.collection_value?.toLocaleString() || 0}`} icon={Gem} color="text-rose-400" />
          <StatCard label="Purchases" value={stats.total_purchases} icon={Archive} color="text-amber-400" />
          <StatCard label="Chases" value={stats.chase_count} icon={Star} color="text-amber-400" />
          <StatCard label="BLKLTD" value={stats.blkltd_count} icon={Star} color="text-purple-400" />
        </div>

        {/* Recent Hauls */}
        {hauls.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-semibold text-zinc-100 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" /> Recent Hauls
            </h2>
            <div data-testid="haul-timeline" className="space-y-3">
              {hauls.map((h) => <HaulCard key={h.post_id} haul={h} showAuthor={false} />)}
            </div>
          </div>
        )}

        {/* Series Completion */}
        {stats.series_completion?.length > 0 && (
          <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
            <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4">Series Completion</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stats.series_completion.slice(0, 10).map((s) => (
                <div key={s.series} className="flex items-center gap-3">
                  <span className="text-xs text-zinc-400 w-24 truncate">{s.series}</span>
                  <Progress value={s.percentage} className="flex-1 h-2" />
                  <span className="text-xs font-mono text-zinc-500 w-14 text-right">{s.owned}/{s.total}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Most Valuable */}
        {stats.most_valuable?.length > 0 && (
          <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
            <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4 flex items-center gap-2">
              <Gem className="w-4 h-4 text-amber-400" /> Most Valuable
            </h2>
            <div className="space-y-2">
              {stats.most_valuable.map((r, i) => (
                <div key={r.release_id} onClick={() => navigate(`/catalog/${r.release_id}`)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 cursor-pointer transition-colors">
                  <span className="text-xs font-mono text-zinc-600 w-6">#{i + 1}</span>
                  {r.model_image ? (
                    <img src={resolveImageUrl(r.model_image)} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center text-[8px] text-zinc-600 font-mono">{r.khmg_number}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200 truncate">{r.release_name}</p>
                    <p className="text-[11px] text-zinc-500">{r.vehicle}</p>
                  </div>
                  <span className="font-mono text-sm text-emerald-400">{r.estimated_market_value != null ? `$${r.estimated_market_value}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collection */}
        <div>
          <h2 className="font-heading text-xl font-semibold text-zinc-100 mb-4 flex items-center gap-2">
            <Archive className="w-5 h-5 text-blue-400" /> Collection ({collection.length})
          </h2>
          <CollectionGrid releases={collection} onNavigate={navigate} />
        </div>

        {/* Wishlist */}
        {wishlist?.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-semibold text-zinc-100 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-400" /> Wishlist ({wishlist.length})
            </h2>
            <WishlistSection wishlist={wishlist} onNavigate={navigate} />
          </div>
        )}

        {/* Recent Activity */}
        {recent_activity?.length > 0 && (
          <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
            <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-500" /> Recent Activity
            </h2>
            <RecentActivity activity={recent_activity} onNavigate={navigate} />
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 border-t border-zinc-800/50">
          <p className="text-zinc-600 text-xs">Powered by <span className="text-rose-500 font-medium">Castfolio</span></p>
          <Button onClick={() => navigate("/login")} variant="link" className="text-zinc-500 text-xs mt-1">
            <ExternalLink className="w-3 h-3 mr-1" /> Start your own collection
          </Button>
        </div>
      </div>
    </div>
  );
}
